export const HOME_CAROUSEL_TYPES = [
  'SpecialOffer',
  'SelectedProducts',
  'VitrinOff',
  'NewProducts',
  'NewVendors',
  'BannerCarouselOne',
  'BannerCarouselTwo',
  'BannerCarouselThree',
] as const;

export type HomeCarouselType = (typeof HOME_CAROUSEL_TYPES)[number];

export type HomeCarouselStyle =
  | 'ScheduledProducts'
  | 'RollingProducts'
  | 'RollingVendors'
  | 'ScheduledProductsWithBanners';

export type HomeCarouselAvailability = 'InStock' | 'OutOfStock' | string;

export interface HomeCarouselProduct {
  productId: number;
  productName: string;
  productSlug: string;
  thumbnailImage: string;
  productUrl: string;
  vendorId: number;
  vendorName: string;
  /** Null when the vendor has a personal domain (see `domain`). */
  vendorSlug: string | null;
  /** Tenant custom domain; null/absent for shared Witro vendors. */
  domain?: string | null;
  price: number | null;
  oldPrice: number | null;
  specialPrice: number | null;
  finalPrice: number | null;
  discountPercent: number;
  availability: HomeCarouselAvailability;
  quantity: number;
  displayOrder: number;
}

export interface HomeCarouselVendorProduct {
  productId: number;
  productName?: string;
  productSlug?: string;
  thumbnailImage: string;
  productUrl?: string;
  vendorId?: number;
  vendorName?: string;
  vendorSlug?: string;
  domain?: string | null;
  price?: number | null;
  oldPrice?: number | null;
  specialPrice?: number | null;
  finalPrice?: number | null;
  discountPercent?: number;
  availability?: HomeCarouselAvailability;
  quantity?: number;
  displayOrder?: number;
}

export interface HomeCarouselVendor {
  vendorId: number;
  vendorName: string;
  /** Null when the vendor has a personal domain (see `domain`). */
  vendorSlug: string | null;
  vendorUrl: string;
  /** Tenant custom domain; null/absent for shared Witro vendors. */
  domain?: string | null;
  vendorLogo?: string;
  thumbnailImage?: string;
  aboutMe?: string;
  shortDescription?: string;
  createdDateTime: string;
  products?: HomeCarouselVendorProduct[];
}

export interface PublicProductPriceRange {
  minimumPrice: number;
  maximumPrice: number;
}

export type PublicProductSort = 'Newest' | 'Cheapest' | 'MostExpensive';

export interface PublicProductFilterQuery {
  hasFreeShipping?: boolean;
  hasDiscount?: boolean;
  minPrice?: number;
  maxPrice?: number;
  sort?: PublicProductSort;
}

export interface HomeCarouselBanner {
  id: number;
  title: string;
  link: string;
  desktopMediaFileUrl: string;
  mobileMediaFileUrl: string;
  displayOrder: number;
}

export interface HomeCarouselItem {
  carouselId: number;
  type: HomeCarouselType;
  style: HomeCarouselStyle;
  title: string;
  listId: number | null;
  listTitle: string | null;
  isDefaultFallback: boolean;
  lookbackDays: number | null;
  fromDateUtc: string | null;
  toDateUtc: string | null;
  products: HomeCarouselProduct[];
  vendors: HomeCarouselVendor[];
  banners: HomeCarouselBanner[];
}

export interface HomeCarouselProductsData {
  count: number;
  carousel: Omit<HomeCarouselItem, 'vendors'> | null;
  priceRange?: PublicProductPriceRange;
}

export interface HomeCarouselsData {
  items: HomeCarouselItem[];
  priceRange?: PublicProductPriceRange;
}

export interface HomeCarouselVendorsData {
  type: HomeCarouselType;
  style: HomeCarouselStyle;
  lookbackDays: number;
  fromDateUtc: string;
  toDateUtc: string;
  count: number;
  vendors: HomeCarouselVendor[];
}

export function isHomeCarouselType(
  value: string | undefined
): value is HomeCarouselType {
  return HOME_CAROUSEL_TYPES.some((type) => type === value);
}

export const PLP_VENDORS_CAROUSEL: HomeCarouselType = 'NewVendors';

export function isVendorsCarousel(
  value: HomeCarouselType | undefined
): value is typeof PLP_VENDORS_CAROUSEL {
  return value === PLP_VENDORS_CAROUSEL;
}
